import React, { useState, useEffect } from 'react';
import Layout from '../components/dashboard/Layout';
import ClientOverview from '../components/ClientOverview';
import { clientsAPI } from '../services/api';
import { Users, Plus, Building2, User, Eye, EyeOff } from 'lucide-react';

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [showClientOverview, setShowClientOverview] = useState(true);
  const [formData, setFormData] = useState({
    client_type: 'corporate',
    email: '',
    phone: '',
    full_name: '',
    id_number: '',
    company_name: '',
    company_address: '',
    tax_id: '',
    contact_person: '',
    contact_position: '',
    address: '',
    notes: '',
    payment_terms: 30
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (client = null) => {
    if (client) {
      setEditMode(true);
      setSelectedClient(client);
      setFormData({
        client_type: client.client_type,
        email: client.email,
        phone: client.phone,
        full_name: client.full_name || '',
        id_number: client.id_number || '',
        company_name: client.company_name || '',
        company_address: client.company_address || '',
        tax_id: client.tax_id || '',
        contact_person: client.contact_person || '',
        contact_position: client.contact_position || '',
        address: client.address,
        notes: client.notes || '',
        payment_terms: client.payment_terms
      });
    } else {
      setEditMode(false);
      setSelectedClient(null);
      setFormData({
        client_type: 'corporate',
        email: '',
        phone: '',
        full_name: '',
        id_number: '',
        company_name: '',
        company_address: '',
        tax_id: '',
        contact_person: '',
        contact_position: '',
        address: '',
        notes: '',
        payment_terms: 30
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedClient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        payment_terms: parseInt(formData.payment_terms)
      };

      if (editMode) {
        await clientsAPI.update(selectedClient.id, submitData);
        alert('Client berhasil diupdate!');
      } else {
        await clientsAPI.create(submitData);
        alert('Client berhasil ditambahkan!');
      }
      
      handleCloseModal();
      loadClients();
    } catch (error) {
      alert('Gagal menyimpan client: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Client Management</h1>
            <p className="text-muted-foreground">Manage semua kategori client dengan data real-time</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowClientOverview(!showClientOverview)}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center space-x-2"
            >
              {showClientOverview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>{showClientOverview ? 'Hide' : 'Show'} Overview</span>
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 btn"
              data-testid="add-client-button"
            >
              <Plus className="w-4 h-4" />
              <span>Add Client</span>
            </button>
          </div>
        </div>

        {/* Client Overview */}
        {showClientOverview && !loading && <ClientOverview clients={clients} />}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading clients...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => (
              <div
                key={client.id}
                onClick={() => handleOpenModal(client)}
                className="glass-card p-6 rounded-xl border border-border/40 hover:border-primary/30 transition-all cursor-pointer"
                data-testid="client-card"
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${
                    client.client_type === 'corporate'
                      ? 'bg-primary/10 border border-primary/30'
                      : 'bg-secondary/10 border border-secondary/30'
                  }`}>
                    {client.client_type === 'corporate' ? (
                      <Building2 className={`w-6 h-6 ${
                        client.client_type === 'corporate' ? 'text-primary' : 'text-secondary'
                      }`} />
                    ) : (
                      <User className={`w-6 h-6 ${
                        client.client_type === 'corporate' ? 'text-primary' : 'text-secondary'
                      }`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">
                      {client.company_name || client.full_name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {client.client_type === 'corporate' ? 'Corporate' : 'Individual'}
                    </p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Rentals:</span>
                        <span className="text-foreground font-medium">{client.total_rentals}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Outstanding:</span>
                        <span className="text-foreground font-medium mono">
                          Rp {(client.outstanding_balance / 1000000).toFixed(1)}M
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && clients.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No clients found</p>
          </div>
        )}

        {/* Add/Edit Client Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="glass-card p-6 rounded-xl border border-border/40 max-w-2xl w-full my-8">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {editMode ? 'Edit Client' : 'Tambah Client Baru'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Client Type */}
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Kategori Client <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={formData.client_type}
                    onChange={(e) => setFormData({...formData, client_type: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    required
                  >
                    <option value="individual">👤 Personal/Perorangan</option>
                    <option value="corporate">🏢 Perusahaan</option>
                    <option value="event_organizer">🎉 Event Organizer</option>
                    <option value="education">🎓 Pendidikan</option>
                    <option value="university">🏫 Universitas</option>
                    <option value="office">🏛️ Kantor</option>
                    <option value="business_entity">🏭 Badan Usaha</option>
                    <option value="cv">📋 CV (Commanditaire Vennootschap)</option>
                    <option value="business_unit">🏬 Unit Usaha</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Email <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="client@example.com"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Phone <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="+6281234567890"
                      required
                    />
                  </div>
                </div>

                {/* Corporate Fields */}
                {formData.client_type === 'corporate' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Nama Perusahaan <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.company_name}
                          onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="PT Tech Solutions"
                          required={formData.client_type === 'corporate'}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          NPWP (Tax ID)
                        </label>
                        <input
                          type="text"
                          value={formData.tax_id}
                          onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="01.234.567.8-901.000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Contact Person <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.contact_person}
                          onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="Nama contact person"
                          required={formData.client_type === 'corporate'}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-2">
                          Posisi Contact
                        </label>
                        <input
                          type="text"
                          value={formData.contact_position}
                          onChange={(e) => setFormData({...formData, contact_position: e.target.value})}
                          className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                          placeholder="Procurement Manager"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-2">
                        Alamat Perusahaan <span className="text-destructive">*</span>
                      </label>
                      <textarea
                        value={formData.company_address}
                        onChange={(e) => setFormData({...formData, company_address: e.target.value})}
                        className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                        rows="2"
                        placeholder="Jl. Sudirman No. 123, Jakarta"
                        required={formData.client_type === 'corporate'}
                      />
                    </div>
                  </>
                )}

                {/* Individual Fields */}
                {formData.client_type === 'individual' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-2">
                        Nama Lengkap <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                        placeholder="John Doe"
                        required={formData.client_type === 'individual'}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-2">
                        No. KTP/ID
                      </label>
                      <input
                        type="text"
                        value={formData.id_number}
                        onChange={(e) => setFormData({...formData, id_number: e.target.value})}
                        className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                        placeholder="3174010101900001"
                      />
                    </div>
                  </div>
                )}

                {/* Common Address */}
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Alamat {formData.client_type === 'corporate' ? 'Penagihan' : ''} <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                    rows="2"
                    placeholder="Alamat lengkap"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Payment Terms */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Payment Terms (hari) <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      required
                    >
                      <option value="14">14 hari</option>
                      <option value="30">30 hari</option>
                      <option value="45">45 hari</option>
                      <option value="60">60 hari</option>
                    </select>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-2">
                      Notes (Opsional)
                    </label>
                    <input
                      type="text"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                      placeholder="Catatan tambahan"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors btn"
                  >
                    {editMode ? 'Update Client' : 'Tambah Client'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientsPage;
